import { createSlice } from '@reduxjs/toolkit';
import {
  PROJECT_TYPES,
  getDefaultDocuments,
  detectProjectType,
} from '../constants/projectTypes';

const initialState = {
  // Mevcut proje tipi
  projectType: PROJECT_TYPES.MULTI_METER,

  // Seçili dokümanlar (kullanıcı tarafından değiştirilebilir)
  selectedDocuments: getDefaultDocuments(PROJECT_TYPES.MULTI_METER),

  // Otomatik tip tespiti aktif mi?
  autoDetect: true,

  // Proje istatistikleri (tip tespiti için)
  stats: {
    floorCount: 1,
    meterCount: 1,
    panelCount: 1,
    hasEnergyRoom: false,
  },
};

const projectTypeSlice = createSlice({
  name: 'projectType',
  initialState,
  reducers: {
    // Proje tipini manuel olarak değiştir
    setProjectType: (state, action) => {
      state.projectType = action.payload;
      // Otomatik tespit kapatılır
      state.autoDetect = false;
      // Yeni tipe göre varsayılan dokümanları seç
      state.selectedDocuments = getDefaultDocuments(action.payload);
    },

    // Proje tipini otomatik tespit et ve güncelle
    autoDetectProjectType: (state) => {
      if (state.autoDetect) {
        const detectedType = detectProjectType(state.stats);
        if (detectedType !== state.projectType) {
          state.projectType = detectedType;
          state.selectedDocuments = getDefaultDocuments(detectedType);
        }
      }
    },

    // Seçili dokümanları güncelle
    setSelectedDocuments: (state, action) => {
      state.selectedDocuments = action.payload;
    },

    // Tek bir dokümanı ekle
    addDocument: (state, action) => {
      const docType = action.payload;
      if (!state.selectedDocuments.includes(docType)) {
        state.selectedDocuments.push(docType);
      }
    },

    // Tek bir dokümanı çıkar
    removeDocument: (state, action) => {
      const docType = action.payload;
      state.selectedDocuments = state.selectedDocuments.filter(d => d !== docType);
    },

    // Doküman toggle (ekle/çıkar)
    toggleDocument: (state, action) => {
      const docType = action.payload;
      if (state.selectedDocuments.includes(docType)) {
        state.selectedDocuments = state.selectedDocuments.filter(d => d !== docType);
      } else {
        state.selectedDocuments.push(docType);
      }
    },

    // Proje istatistiklerini güncelle
    updateProjectStats: (state, action) => {
      state.stats = { ...state.stats, ...action.payload };
      // Otomatik tespit aktifse proje tipini güncelle
      if (state.autoDetect) {
        const detectedType = detectProjectType(state.stats);
        if (detectedType !== state.projectType) {
          state.projectType = detectedType;
          state.selectedDocuments = getDefaultDocuments(detectedType);
        }
      }
    },

    // Kat sayısını güncelle
    setFloorCount: (state, action) => {
      state.stats.floorCount = action.payload;
      if (state.autoDetect) {
        const detectedType = detectProjectType(state.stats);
        if (detectedType !== state.projectType) {
          state.projectType = detectedType;
          state.selectedDocuments = getDefaultDocuments(detectedType);
        }
      }
    },

    // Sayaç sayısını güncelle
    setMeterCount: (state, action) => {
      state.stats.meterCount = action.payload;
      if (state.autoDetect) {
        const detectedType = detectProjectType(state.stats);
        if (detectedType !== state.projectType) {
          state.projectType = detectedType;
          state.selectedDocuments = getDefaultDocuments(detectedType);
        }
      }
    },

    // Panel sayısını güncelle
    setPanelCount: (state, action) => {
      state.stats.panelCount = action.payload;
      if (state.autoDetect) {
        const detectedType = detectProjectType(state.stats);
        if (detectedType !== state.projectType) {
          state.projectType = detectedType;
          state.selectedDocuments = getDefaultDocuments(detectedType);
        }
      }
    },

    // Enerji odası durumunu güncelle
    setHasEnergyRoom: (state, action) => {
      state.stats.hasEnergyRoom = action.payload;
      if (state.autoDetect) {
        const detectedType = detectProjectType(state.stats);
        if (detectedType !== state.projectType) {
          state.projectType = detectedType;
          state.selectedDocuments = getDefaultDocuments(detectedType);
        }
      }
    },

    // Otomatik tespit modunu aç/kapat
    setAutoDetect: (state, action) => {
      state.autoDetect = action.payload;
      // Eğer açıldıysa hemen tespit et
      if (action.payload) {
        const detectedType = detectProjectType(state.stats);
        state.projectType = detectedType;
        state.selectedDocuments = getDefaultDocuments(detectedType);
      }
    },

    // Varsayılanlara sıfırla
    resetToDefaults: (state) => {
      state.selectedDocuments = getDefaultDocuments(state.projectType);
    },

    // Tamamen sıfırla
    resetProjectType: () => initialState,
  },
});

export const {
  setProjectType,
  autoDetectProjectType,
  setSelectedDocuments,
  addDocument,
  removeDocument,
  toggleDocument,
  updateProjectStats,
  setFloorCount,
  setMeterCount,
  setPanelCount,
  setHasEnergyRoom,
  setAutoDetect,
  resetToDefaults,
  resetProjectType,
} = projectTypeSlice.actions;

export default projectTypeSlice.reducer;

// Selectors
export const selectProjectType = (state) => state.projectType.projectType;
export const selectSelectedDocuments = (state) => state.projectType.selectedDocuments;
export const selectAutoDetect = (state) => state.projectType.autoDetect;
export const selectProjectStats = (state) => state.projectType.stats;
